import React, { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { browserHistory } from 'utils/navigation'
import { Button, Container, Loader, TextOverflow } from 'components/UIElements'
import { convertToLocalizedNumber } from 'utils/misc'
import { buttonContentOnSendState } from 'utils/helpers'
import { getFormattedDateString, getDateFromNow } from 'utils/time'
import STRINGS from 'utils/strings'
import 'react-datepicker/dist/react-datepicker.css'
import { renderFunctions } from '../../../../../Payments/components/PaymentsTable'
import CreateCaregiverNav from './CreateCaregiverNav'
import ParticipantStats from '../../../CreateParticipant/components/ParticipantStats'
import ParticipantProfile from '../../../CreateParticipant/components/ParticipantProfile'
import '../../../CreateParticipant/stylesheets/participant.scss'
import AccountSettings from '../../../CreateParticipant/components/AccountSettings'
import { onOpenCancelModal } from '../../../../utils/pariticipantUtils'
import DrawerTabs from '../../../CreateParticipant/components/DrawerTabs'
import StudyTimeline from '../../../CreateParticipant/components/StudyTimeline'
import { validateCaregiver } from '../../CreateCaregiver/modules/CreateCaregiver'
import ParticipantHistory from '../../../CreateParticipant/components/ParticipantHistory'
import CaregiverForm from './CaregiverForm'

const CreateCaregiverPage = props => {
  const {
    isCreate,
    ptpId: caregiverId,
    canSeeOrphans,
    participant: caregiver,
    inLeafSite,
    onSetOrphanedParticipants,
    study,
    isClinOrSiteCoordinator = false,
  } = props
  const pageRef = useRef()

  const [editable, setEditable] = useState(isCreate || !!caregiverId)
  const [language, setLanguage] = useState('en-US')

  useEffect(() => {
    const {
      fetchPtp,
      fetchPtpPayments,
      hasTreasureChest,
      inDrawer,
      inLeafSite,
      isInParticipantsTable,
      onSetOrphanedParticipants,
      params,
      ptpSiteID,
      setNavBarProps,
      study,
      toggleOnSite,
    } = props

    // Only set orphaned participants if there are leaf sites in the current study version
    const { leafSites } = props
    const hasLeafSites = leafSites && leafSites.length > 0
    if (canSeeOrphans && !isCreate && !inLeafSite && !inDrawer && hasLeafSites) {
      onSetOrphanedParticipants(true)
    }

    setLanguage(navigator.language)

    const { studyID, studyVersionID } = params

    if (((caregiverId && inDrawer) || !isInParticipantsTable) && !isCreate) {
      fetchPtp(studyID, ptpSiteID, caregiverId)
      fetchPtpPayments(studyID, caregiverId, hasTreasureChest)
    }

    setNavBarProps({
      back: `/studies/${study.id}/${studyVersionID}/participants`,
      close: `/studies/${study.id}/${studyVersionID}/participants`,
      backText: 'Caregivers',
    })

    if (caregiverId) {
      toggleOnSite(ptpSiteID)
    }
    pageRef.current.scrollIntoView(true)
    return () => {
      const {
        resetCheckedSites,
        params: { participantId },
      } = props
      setNavBarProps(null)
      if (participantId) resetCheckedSites()
    }
  }, [])

  useEffect(() => {
    const { leafSites } = props
    const ptpLoaded = Object.keys(caregiver).length !== 0
    const hasLeafSites = leafSites && leafSites.length > 0
    // Only set orphaned participants if there are leaf sites in the current study version
    if (canSeeOrphans && ptpLoaded && !!caregiverId && !inLeafSite && hasLeafSites) {
      onSetOrphanedParticipants(true)
    }
  }, [canSeeOrphans, caregiver, inLeafSite, onSetOrphanedParticipants, caregiverId, props.leafSites])

  const triggerEditable = () => {
    setEditable(true)
  }

  const getCompletion = () => {
    const { participant } = props
    const { num_completed_instruments, num_total_instruments } = participant
    const completed = num_completed_instruments || 0
    const total = num_total_instruments || 100
    return `${Math.floor((completed / total) * 100)}% Compliance`
  }

  const onSaveCaregiver = () => {
    const {
      isEmailless,
      baseSiteID,
      saveToDatabase,
      study,
      participant,
      checkedCohorts,
      consentRequired,
      checkedSites,
      caregiverDataFields,
      params: { participantId, studyVersionID },
      studyID,
    } = props

    saveToDatabase({
      studyID: study.id,
      siteID: baseSiteID,
      participant,
      checkedCohorts,
      participantID: participant.id,
      consentRequired,
      sites: checkedSites,
      pDataFields: caregiverDataFields,
      redirect:
        !participantId && isEmailless
          ? `/studies/${studyID}/${studyVersionID}/participants/caregiver/account-settings`
          : null,
    })
  }

  const onValidateParticipant = () => {
    const { participant, caregiverDataFields } = props

    return validateCaregiver({
      participant,
      pDataFields: caregiverDataFields,
      isPatch: !isCreate,
    })
  }

  const renderAccountDeletion = (deactivationRequestTimestamp, canSeeAccountSettings) => {
    const deletionMessage = `Account deletion request received ${getFormattedDateString(
      deactivationRequestTimestamp,
      'DD/MM/YYYY',
    )} ( ${getDateFromNow(deactivationRequestTimestamp)} ). Please review the participant's account settings.`
    return (
      <>
        {deactivationRequestTimestamp && (
          <div className='account-deletion'>
            <h6>Account Deletion Request</h6>
            <p>{deletionMessage}</p>
          </div>
        )}
        {canSeeAccountSettings && <AccountSettings {...props} isCaregiver={true} />}
      </>
    )
  }

  const {
    canAddPtp,
    canResendInviteOrResetPw,
    canWrite,
    closeDrawer,
    inDrawer,
    link,
    locales,
    noPII,
    caregiverDataFields,
    requestSending,
    requestSent,
    sendDisabled,
    studyLock,
    ptpInstrumentsLoading,
    instrumentStatus,
    isSubjectIdAvailable,
    hideDinamically,
  } = props
  const { fname, lname, id, deactivation_request_timestamp } = caregiver
  // Only show need to reassign if there are leaf sites in the current study version
  const hasLeafSites = props.leafSites && props.leafSites.length > 0
  const needToReassign = !isCreate && canSeeOrphans && !caregiver?.in_leaf_site && hasLeafSites
  const canSave = canWrite || (isCreate && canAddPtp)
  renderFunctions.amount = ({ value }) => {
    const localizedPayment = convertToLocalizedNumber(value, language, true)
    return (
      <td key={`amount-${value}`} className='amount'>
        {localizedPayment}
      </td>
    )
  }

  const addCaregiver = {
    content: 'Add This Caregiver',
    sendingContent: 'Adding',
    sentContent: '...Added!',
  }
  const saveChanges = {
    content: 'Save Changes',
    sendingContent: 'Saving',
    sentContent: '...Saved!',
  }
  const sendStateClassName = sendDisabled ? 'send-state' : '' // for cursor wait during sendState

  const renderParticipantInfo = () => {
    return (
      <>
        <div className='caregiver-info'>
          <TextOverflow text={caregiver.fname} tagClassName='no-margin title-overflow' maxWidth={300} />
          <h4 className='text-blue'>Caregiver ID: {id}</h4>
          {!isCreate && !noPII && (
            <>
              <span className='text text-blue'>Subject ID: {caregiver.subject_id || '---'}</span>
            </>
          )}
        </div>
      </>
    )
  }

  const TAB_IDS = {
    accountSettings: 'account-settings',
  }

  const canViewAccountSettings = canResendInviteOrResetPw || canWrite || canResendInviteOrResetPw

  const TABS = [
    {
      id: TAB_IDS.accountSettings,
      title: STRINGS.accountSettings,
      visible: inDrawer && canViewAccountSettings,
    },
  ]

  const visibleTabs = useMemo(() => {
    return TABS.filter(tab => tab.visible)
  }, [TABS])

  const [tab, setTab] = useState()

  useEffect(() => {
    if (!tab && ptpInstrumentsLoading === false) {
      setTab(visibleTabs[0])
    }
  }, [visibleTabs, tab, ptpInstrumentsLoading, instrumentStatus?.length])

  useEffect(() => {
    return () => {
      setTab(null)
    }
  }, [])

  const isValidPtp = onValidateParticipant()

  const isSubmitBtnDisabled = sendDisabled || studyLock || isSubjectIdAvailable === false || !isValidPtp

  return (
    <div className={`participant page ${sendStateClassName} ${inDrawer ? 'drawer-container' : ''}`} ref={pageRef}>
      {isCreate && <CreateCaregiverNav {...props} />}
      {isCreate || id ? (
        <>
          <div className={inDrawer ? 'ptp-drawer-buttons' : ''}>
            {inDrawer && !isClinOrSiteCoordinator && (
              <div className='flexed-header compliance'>
                <span className='compliance-rate'>
                  <b>{getCompletion()}</b>
                </span>
              </div>
            )}
            {link && canWrite && (
              <div className='flexed-header end-justified'>
                <Button
                  disabled={studyLock}
                  grey
                  id='participant-edit-profile'
                  content='Edit Profile'
                  onClick={() => {
                    closeDrawer()
                    browserHistory.push(link)
                  }}
                />
              </div>
            )}
          </div>
          <div className={inDrawer ? 'ptp-sticky-info' : ''}>
            {isCreate ? (
              <div className='flexed-header'>
                <h2 className='ptp-name'>New Caregiver</h2>
              </div>
            ) : (
              renderParticipantInfo()
            )}
          </div>
          <div className={`ptp-container-wrapper ${inDrawer ? 'drawer-container-wrapper' : ''}`}>
            {inDrawer ? (
              <>
                <div className='drawer-info-section'>
                  <StudyTimeline study={study} participant={caregiver} />
                  {!isCreate && <ParticipantStats isClinOrSiteCoordinator={isClinOrSiteCoordinator} {...props} />}
                </div>
                <DrawerTabs
                  tabs={visibleTabs}
                  selectedTab={tab}
                  selectTab={newTab => {
                    setTab(newTab)
                  }}
                />
                <div className='drawer-tab-content'>
                  {tab?.id == TAB_IDS.accountSettings && hideDinamically.accountSettings && (
                    <ParticipantProfile
                      {...props}
                      accountSettings={renderAccountDeletion(
                        deactivation_request_timestamp,
                        canWrite || canResendInviteOrResetPw,
                      )}
                    />
                  )}
                  {tab?.id == TAB_IDS.history ? <ParticipantHistory {...props} /> : null}
                </div>
              </>
            ) : (
              <>
                {!isCreate && <ParticipantStats isClinOrSiteCoordinator={isClinOrSiteCoordinator} {...props} />}
                <CaregiverForm
                  disabled={!(canWrite || canAddPtp) || sendDisabled}
                  needToReassign={needToReassign}
                  editable={editable}
                  triggerEditable={triggerEditable}
                  {...props}
                  locales={
                    study.config && study.config.enforced_language ? study.config.enforced_language.languages : locales
                  }
                  caregiverDataFields={caregiverDataFields}
                  studyId={study?.id}
                  isSubjectIdAvailable={isSubjectIdAvailable}
                  participantId={caregiverId}
                />
                {editable && canSave && (
                  <div className='button-list'>
                    <Button
                      onClick={() => onOpenCancelModal(props)}
                      className='grey'
                      id='create-participant-cancel'
                      content='Cancel'
                      disabled={sendDisabled}
                    />
                    <Button
                      onClick={onSaveCaregiver}
                      id='participant-save'
                      content={buttonContentOnSendState(
                        isCreate ? addCaregiver : saveChanges,
                        requestSending,
                        requestSent,
                      )}
                      disabled={isSubmitBtnDisabled}
                      loading={requestSending}
                    />
                  </div>
                )}
                {!isCreate && hideDinamically.accountSettings && <AccountSettings {...props} />}
              </>
            )}
          </div>
        </>
      ) : (
        <Loader />
      )}
    </div>
  )
}

CreateCaregiverPage.propTypes = {
  baseSiteID: PropTypes.number,
  canAddPtp: PropTypes.bool,
  canMove: PropTypes.bool,
  canResendInviteOrResetPw: PropTypes.bool,
  canSeeOrphans: PropTypes.bool,
  canUpdateConsent: PropTypes.bool,
  canViewAnnouncements: PropTypes.bool,
  canViewFiles: PropTypes.bool,
  canViewVisitSchedule: PropTypes.bool,
  canWrite: PropTypes.bool,
  checkedCohorts: PropTypes.object,
  checkedSites: PropTypes.object,
  clearPtpVisits: PropTypes.func,
  closeDrawer: PropTypes.func,
  closeModal: PropTypes.func,
  componentLoading: PropTypes.shape({ pendingPayments: PropTypes.bool }),
  consentRequired: PropTypes.bool,
  fetchConsentEvents: PropTypes.func,
  fetchLocales: PropTypes.func,
  fetchParticipantVisits: PropTypes.func,
  fetchPtp: PropTypes.func,
  fetchPtpPayments: PropTypes.func,
  fetchVisitTemplates: PropTypes.func,
  hasIncentives: PropTypes.bool,
  hasOrphanedParticipants: PropTypes.bool,
  hasTreasureChest: PropTypes.bool,
  hasVisitSupport: PropTypes.bool,
  inDrawer: PropTypes.bool,
  initializeEditPage: PropTypes.func,
  inLeafSite: PropTypes.bool,
  isCreate: PropTypes.bool,
  isDiary: PropTypes.bool,
  isEmailless: PropTypes.bool,
  isInParticipantsTable: PropTypes.bool,
  leafSites: PropTypes.arrayOf(PropTypes.number),
  link: PropTypes.string,
  locales: PropTypes.arrayOf(PropTypes.object),
  noTracksFeature: PropTypes.bool,
  noPII: PropTypes.bool,
  onSetOrphanedParticipants: PropTypes.func,
  openModal: PropTypes.func,
  params: PropTypes.object,
  params: PropTypes.object,
  participant: PropTypes.object,
  participantDataFields: PropTypes.object,
  participantVisits: PropTypes.arrayOf(PropTypes.array),
  payments: PropTypes.object,
  ptpId: PropTypes.number,
  ptpSiteID: PropTypes.number,
  requestSending: PropTypes.bool,
  requestSent: PropTypes.bool,
  resetCheckedSites: PropTypes.func,
  saveToDatabase: PropTypes.func,
  sendDisabled: PropTypes.bool,
  setNavBarProps: PropTypes.func,
  sitesInfo: PropTypes.shape(),
  study: PropTypes.object,
  studyID: PropTypes.number,
  studyLock: PropTypes.bool,
  toggleOnSite: PropTypes.func,
  toggleSite: PropTypes.func,
  updateField: PropTypes.func,
  userId: PropTypes.number,
  visitTemplates: PropTypes.arrayOf(PropTypes.object),
}

export default CreateCaregiverPage
